import { useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  /** Never return less than this, so a short viewport keeps the previous fixed sizing. */
  minHeight: number;
  /** Slack for margins between the stacked panels, which offsetHeight does not include. */
  gap?: number;
}

interface Result {
  /** Attach to the list. A callback ref, so the list may mount after its data arrives. */
  ref: (node: HTMLElement | null) => void;
  height: number;
}

/** Nearest ancestor that scrolls, which is what bounds the space a list can grow into. */
const scrollParent = (element: HTMLElement): HTMLElement => {
  let node = element.parentElement;

  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }

  return document.documentElement;
};

/**
 * How much of the scroll container is taken by everything laid out after `element`.
 *
 * Measured by walking the following siblings up the ancestor chain rather than from the
 * container's scrollHeight, because scrollHeight includes `element` itself: sizing against it
 * would feed the height we just applied back into the next measurement.
 */
const spaceBelow = (element: HTMLElement, container: HTMLElement): number => {
  let total = 0;
  let node: HTMLElement | null = element;

  while (node && node !== container && node !== document.body) {
    let sibling = node.nextElementSibling as HTMLElement | null;
    while (sibling) {
      total += sibling.offsetHeight;
      sibling = sibling.nextElementSibling as HTMLElement | null;
    }
    node = node.parentElement;
  }

  return total;
};

/**
 * The height a scrolling list may take to reach the bottom of its scroll container without
 * pushing what sits below it out of view.
 *
 * Lists in the side panels were capped at a hand-picked pixel height, so a tall monitor showed
 * the same handful of rows as a laptop and left the rest of the column empty. This measures the
 * space that is actually free and lets the list use it, falling back to `minHeight` when there
 * is none to spare.
 */
export const useAvailableListHeight = ({ minHeight, gap = 8 }: Options): Result => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(minHeight);
  const frameRef = useRef<number | null>(null);

  const measure = useCallback(() => {
    if (!element) return;

    const container = scrollParent(element);
    const containerBox = container.getBoundingClientRect();
    const style = getComputedStyle(container);
    const paddingBottom = parseFloat(style.paddingBottom) || 0;

    // Relative to the container's own content, so the result does not change as it is scrolled.
    const offsetTop = element.getBoundingClientRect().top - containerBox.top + container.scrollTop;
    const usable =
      container.clientHeight - paddingBottom - offsetTop - spaceBelow(element, container) - gap;

    setHeight(prev => {
      const next = Math.max(minHeight, Math.floor(usable));
      // A sub-pixel difference is not worth a re-render, and re-rendering on every observer
      // callback is what turns a resize into a loop.
      return Math.abs(prev - next) < 2 ? prev : next;
    });
  }, [element, minHeight, gap]);

  const schedule = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    if (!element) return;

    schedule();

    const container = scrollParent(element);
    const resizeObserver = new ResizeObserver(schedule);

    // The container itself has a fixed height in both side panels, so watching only it would
    // catch a window resize and nothing else. Its children are the stacked panels, and one of
    // them growing once its data arrives is what changes the space left over.
    const observeChildren = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(container);
      Array.from(container.children).forEach(child => resizeObserver.observe(child));
    };

    observeChildren();

    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      schedule();
    });
    mutationObserver.observe(container, { childList: true });

    window.addEventListener('resize', schedule);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [element, schedule]);

  return { ref: setElement, height };
};

export default useAvailableListHeight;
